'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { isFilled } from '@prismicio/helpers';
import { PrismicNextImage } from '@prismicio/next';
import { Autoplay, Keyboard } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ConditionalLink } from '@/app/lib/ConditionalLink';
import { BackgroundImage } from "@/app/lib/ParallaxBackground";
import { MoreLink } from '@/app/lib/MoreLink';
import { RevealTitle } from '@/app/lib/RevealTitle';
import { dashify } from '@/app/lib/utils';
import { sliceStyle } from '@/app/lib/utils';
import sharedStyles from '@/app/shared.module.scss';
import styles from './carousel.module.scss';

import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/keyboard';

const Carousel = ({ slice }) => {
	const slides = useMemo(() => slice?.primary?.slide?.filter((item) => isFilled.image(item?.image)) ?? [], [slice?.primary?.slide]);
	const isIndefiniteLoop = slice?.variation === 'indefiniteLoop';
	const hoverIntervalRef = useRef(null);

	const [swiper, setSwiper] = useState(null);
	const [activeIndex, setActiveIndex] = useState(0);
	const [virtualIndices, setVirtualIndices] = useState([]);
	const [zoneCursor, setZoneCursor] = useState('');

	if (slides.length === 0) return null;

	const baseCount = slides.length;
	const renderIndices = isIndefiniteLoop ? virtualIndices : slides.map((_, index) => index);
	const showEmptySlides = slice?.primary?.show_empty_slides !== false;

	const stopHoverScroll = useCallback(() => {
		if (hoverIntervalRef.current) {
			clearInterval(hoverIntervalRef.current);
			hoverIntervalRef.current = null;
		}
	}, []);

	const initialSlide = slice?.primary?.initial_slide && slice?.primary?.slide[slice?.primary?.initial_slide - 1]
		? slice?.primary?.initial_slide - 1
		: 0;

	useEffect(() => {
		if (!isIndefiniteLoop || baseCount === 0) {
			setVirtualIndices([]);
			return;
		}

		const initial = Array.from({ length: baseCount * 3 }, (_, index) => index);
		setVirtualIndices(initial);
		setActiveIndex(0);
	}, [isIndefiniteLoop, baseCount]);

	useEffect(() => {
		if (!isIndefiniteLoop || !swiper || virtualIndices.length === 0) return;
		swiper.slideTo(0, 0, false);
	}, [isIndefiniteLoop, swiper, virtualIndices.length]);

	const handleActiveIndexChange = useCallback((swiperInstance) => {
		const currentIndex = swiperInstance.activeIndex;
		setActiveIndex(currentIndex);

		if (!isIndefiniteLoop || baseCount === 0) return;

		const preloadThreshold = baseCount * 2;

		if (currentIndex > virtualIndices.length - preloadThreshold - 1) {
			setVirtualIndices((prev) => {
				const start = prev.length;
				const appended = Array.from({ length: baseCount * 2 }, (_, i) => start + i);
				return [...prev, ...appended];
			});
		}
	}, [isIndefiniteLoop, baseCount, virtualIndices.length]);

	const handleZoneClick = useCallback((event) => {
		if (!isIndefiniteLoop || !swiper) return;

		const x = event.clientX;
		const leftZone = window.innerWidth * 0.2;
		const rightZone = window.innerWidth * 0.8;

		if (x <= leftZone) {
			if (swiper.activeIndex > 0) swiper.slidePrev();
		} else if (x >= rightZone) {
			swiper.slideNext();
		}
	}, [isIndefiniteLoop, swiper]);

	const handleZoneMouseMove = useCallback((event) => {
		if (!isIndefiniteLoop) return;

		const x = event.clientX;
		const leftZone = window.innerWidth * 0.2;
		const rightZone = window.innerWidth * 0.8;

		if (x <= leftZone) setZoneCursor('left');
		else if (x >= rightZone) setZoneCursor('right');
		else setZoneCursor('');
	}, [isIndefiniteLoop]);

	const handleZoneMouseLeave = useCallback(() => {
		if (!isIndefiniteLoop) return;
		setZoneCursor('');
	}, [isIndefiniteLoop]);

	useEffect(() => {
		return () => stopHoverScroll();
	}, [stopHoverScroll]);

	return (
		<BackgroundImage slicePrimary={slice?.primary}>
			<div className={clsx(styles.wrapper)} style={sliceStyle(slice?.primary)}>

				{/* title */}
				<div className={clsx(sharedStyles.top)}>
					<div className={clsx(sharedStyles.left)}>
						{isFilled.keyText(slice?.primary?.title) && (
							<h1 className={clsx(sharedStyles.h1)} id={dashify(slice?.primary?.title)}>
								<RevealTitle size='small'>{slice?.primary?.title}</RevealTitle>
							</h1>
						)}
					</div>

					{isFilled.link(slice?.primary?.more_link) && (
						<MoreLink link={slice?.primary?.more_link} slice={slice} />
					)}
				</div>

				<div
					onClick={handleZoneClick}
					onMouseMove={handleZoneMouseMove}
					onMouseLeave={handleZoneMouseLeave}
					className={clsx(
						isIndefiniteLoop && zoneCursor === 'left' && 'cursor-scroll cursor-scroll-left',
						isIndefiniteLoop && zoneCursor === 'right' && 'cursor-scroll cursor-scroll-right'
					)}
				>
					<Swiper
						className={clsx('swiper', styles.swiper)}
						modules={[Autoplay, Keyboard]}
						onSwiper={setSwiper}
						onActiveIndexChange={handleActiveIndexChange}
						initialSlide={isIndefiniteLoop ? 0 : initialSlide}
						centeredSlides={isIndefiniteLoop ? false : showEmptySlides}
						spaceBetween={30}
						slidesPerView={'auto'}
						keyboard={{
							enabled: true,
						}}
						watchSlidesProgress={isIndefiniteLoop}
						{...(slice?.primary?.autoplay && !isIndefiniteLoop && {
							autoplay: {
								delay: 5000,
								pauseOnMouseEnter: false,
							},
						})}
						loop={false}
						pagination={slice?.primary?.show_pagination ? { type: 'fraction' } : false}
						breakpoints={{
							0: {
								spaceBetween: 10,
								...(((isIndefiniteLoop) || (!showEmptySlides && !isIndefiniteLoop)) && {
									slidesOffsetBefore: 20,
									slidesOffsetAfter: 20,
								}),
							},
							1024: {
								spaceBetween: 30,
								...(((isIndefiniteLoop) || (!showEmptySlides && !isIndefiniteLoop)) && {
									slidesOffsetBefore: 70,
									slidesOffsetAfter: 70,
								}),
							}
						}}
					>
						{renderIndices.map((virtualIndex) => {
							const baseIndex = isIndefiniteLoop
								? ((virtualIndex % baseCount) + baseCount) % baseCount
								: virtualIndex;
							const item = slides[baseIndex];
							const index = virtualIndex;
							const isCenterSlide = index === activeIndex;
							const isLeftSlide = index < activeIndex;
							const isRightSlide = index > activeIndex;

							return (
								<SwiperSlide
									className={clsx(
										styles.slide,
										slice?.primary?.height === 'Tall' ? styles.tall : styles.short,
										{
											'cursor-scroll': !isIndefiniteLoop && !isCenterSlide,
											'cursor-scroll cursor-scroll-left': !isIndefiniteLoop && isLeftSlide,
											'cursor-scroll cursor-scroll-right': !isIndefiniteLoop && isRightSlide,
										}
									)}
									key={`${baseIndex}-${index}`}
									data-carousel-index={index}
								>
									<ConditionalLink
										url={item?.link}
										condition={
											isIndefiniteLoop
												? (item?.link?.uid || item?.link?.url) !== undefined
												: isCenterSlide && (item?.link?.uid || item?.link?.url) !== undefined
										}
										className={styles.link}
									>
										<PrismicNextImage
											className={clsx(styles.image)}
											field={item?.image}
											fallbackAlt=''
										/>
										<div className={clsx(styles.content)}>
											{isFilled.keyText(item?.title) && <h3>{item?.title}</h3>}
											{isFilled.keyText(item?.subtitle) && <p>{item?.subtitle}</p>}
										</div>
									</ConditionalLink>
								</SwiperSlide>
							);
						})}
					</Swiper>
				</div>

				{isFilled.link(slice?.primary?.more_link) && (
					<MoreLink link={slice?.primary?.more_link} slice={slice} mobile={true} />
				)}

			</div>
		</BackgroundImage>
	);
};

export default Carousel;
